import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Clock3, MapPin } from 'lucide-react';
import { eventDetailHref, formatEventCardDate, type ChurchEvent } from '../lib/calendar';
import { EventCover } from './EventCover';
import { RecurrenceBadge, RegistrationBadge } from './EventMeta';

export function EventsSlider({ events }: { events: ChurchEvent[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slides = events.slice(0, 8);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const update = () => {
      const width = track.clientWidth || 1;
      setIndex(Math.round(track.scrollLeft / width));
    };

    track.addEventListener('scroll', update, { passive: true });
    return () => track.removeEventListener('scroll', update);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = window.setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const next = (index + 1) % slides.length;
      track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
    }, 7000);

    return () => window.clearInterval(timer);
  }, [index, paused, slides.length]);

  const goTo = (target: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (target + slides.length) % slides.length;
    track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
  };

  if (!slides.length) return null;

  return (
    <div
      className="events-slider reveal"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="events-slider-viewport">
        <div
          className="events-slider-track"
          ref={trackRef}
          tabIndex={0}
          aria-label="Próximos eventos"
        >
          {slides.map((event, slideIndex) => (
            <EventSlide
              key={`${event.uid}-${event.start.toISOString()}`}
              event={event}
              active={slideIndex === index}
            />
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <div className="events-slider-controls">
            <button type="button" className="events-slider-arrow" onClick={() => goTo(index - 1)} aria-label="Evento anterior">
              <ArrowLeft size={18} aria-hidden="true" />
            </button>
            <button type="button" className="events-slider-arrow" onClick={() => goTo(index + 1)} aria-label="Evento siguiente">
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="events-slider-dots" role="tablist" aria-label="Elegir evento">
            {slides.map((event, slideIndex) => (
              <button
                key={`${event.uid}-dot`}
                type="button"
                role="tab"
                aria-selected={slideIndex === index}
                aria-label={`Ir al evento ${slideIndex + 1}`}
                className={slideIndex === index ? 'is-active' : ''}
                onClick={() => goTo(slideIndex)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EventSlide({ event, active }: { event: ChurchEvent; active: boolean }) {
  const date = formatEventCardDate(event);
  const cover = event.images[0];
  const href = eventDetailHref(event);

  return (
    <article className={`events-slide ${cover ? '' : 'is-fallback'}`} aria-hidden={!active}>
      <EventCover
        src={cover}
        className="events-slide-media"
        fallback={<div className="events-slide-orb" />}
      >
        <div className="events-slide-overlay" />
      </EventCover>
      <div className="events-slide-copy">
        <time className="event-date-badge event-date-badge-lg" dateTime={event.start.toISOString()}>
          <strong>{date.day}</strong>
          <span>{date.month}</span>
        </time>
        <RecurrenceBadge event={event} />
        <RegistrationBadge event={event} />
        <p className="events-slide-kicker">
          {date.weekday} · {date.time}
        </p>
        <h3>{event.title}</h3>
        {event.excerpt && <p className="events-slide-excerpt">{event.excerpt}</p>}
        <ul className="events-slide-facts">
          <li>
            <Clock3 size={17} aria-hidden="true" />
            {date.longDate}
          </li>
          {event.location && (
            <li>
              <MapPin size={17} aria-hidden="true" />
              {event.location}
            </li>
          )}
        </ul>
        <a className="button button-gold" href={href}>
          Ver detalles <ArrowRight size={18} aria-hidden="true" />
        </a>
      </div>
    </article>
  );
}
